import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  IconBtn,
  PageHeader,
  Spinner,
  useConfirm,
  useIsMobile,
  useToast,
} from "../../design-system";
import {
  cancelAbsence,
  getAbsences,
  getEmployeeAbsenceBalance,
  getHolidaysByGroup,
  getEmployeeActiveContract,
} from "../../helpers/kronaly_helper";
import useCurrentMembership from "../../helpers/useCurrentMembership";
import NewAbsenceModal from "../../Components/Kronaly/Ausencias/NewAbsenceModal";
import {
  ABSENCE_STATUS_LABEL,
  ABSENCE_STATUS_TONE,
  ABSENCE_TYPE_LABEL,
  ABSENCE_TYPE_TONE,
  formatDays,
  formatRange,
} from "../../Components/Kronaly/Ausencias/absenceHelpers";
import { useStartTour } from "../../tours/TourContext";
import { ymdToLocalDate, localDateToYmd } from "../../helpers/datetime";

const unwrap = (r) => {
  const inner = r?.data ?? r;
  return inner?.data ?? inner;
};

const MESES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];
const DIAS_SEM = ["lu", "ma", "mi", "ju", "vi", "sá", "do"];

function firstDayOffset(year, monthIdx) {
  const d = new Date(year, monthIdx, 1);
  const js = d.getDay();
  return js === 0 ? 6 : js - 1;
}
function daysInMonth(year, monthIdx) {
  return new Date(year, monthIdx + 1, 0).getDate();
}

function Ausencias() {
  document.title = "Ausencias | Kronaly";
  const toast = useToast();
  const confirm = useConfirm();
  const isMobile = useIsMobile();
  useStartTour("ausencias");

  const { employeeProfileId, loading: bootLoading, error: bootError } = useCurrentMembership();

  const [year, setYear] = useState(new Date().getFullYear());

  const [balance, setBalance] = useState(null);
  const [absences, setAbsences] = useState([]);
  const [holidaysByDate, setHolidaysByDate] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [showNew, setShowNew] = useState(false);

  const loadBalance = useCallback(async (eid) => {
    if (!eid) return;
    try {
      const r = await getEmployeeAbsenceBalance(eid);
      setBalance(unwrap(r));
    } catch (_) {
      setBalance(null);
    }
  }, []);

  const loadAbsences = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await getAbsences({ include_team: "true" });
      setAbsences(unwrap(r) || []);
    } catch (e) {
      setError(typeof e === "string" ? e : e?.message || "No se pudieron cargar las ausencias.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCalendarHolidays = useCallback(async (eid) => {
    if (!eid) return;
    try {
      const acRes = await getEmployeeActiveContract(eid);
      const ac = unwrap(acRes);
      const holidayIds = Array.isArray(ac?.current_terms?.holiday_group_ids)
        ? ac.current_terms.holiday_group_ids
        : [];
      if (holidayIds.length === 0) {
        setHolidaysByDate({});
        return;
      }
      const all = await Promise.all(holidayIds.map((id) => getHolidaysByGroup(id).catch(() => null)));
      const map = {};
      all.forEach((res) => {
        const list = unwrap(res) || [];
        (Array.isArray(list) ? list : []).forEach((h) => {
          if (h?.date) map[h.date] = h.name;
        });
      });
      setHolidaysByDate(map);
    } catch (_) {
      setHolidaysByDate({});
    }
  }, []);

  useEffect(() => {
    if (!employeeProfileId) return;
    loadBalance(employeeProfileId);
    loadAbsences();
    loadCalendarHolidays(employeeProfileId);
  }, [employeeProfileId, loadBalance, loadAbsences, loadCalendarHolidays]);

  const myAbsences = useMemo(
    () => absences.filter((a) => a.employee_profile_id === employeeProfileId),
    [absences, employeeProfileId],
  );

  const upcoming = useMemo(() => {
    const today = localDateToYmd(new Date());
    return myAbsences
      .filter((a) => a.status !== "cancelled" && a.status !== "rejected" && a.end_date >= today)
      .sort((a, b) => a.start_date.localeCompare(b.start_date))
      .slice(0, 4);
  }, [myAbsences]);

  const breakdownByType = useMemo(() => {
    const acc = {};
    myAbsences.forEach((a) => {
      if (a.status !== "approved") return;
      acc[a.type] = (acc[a.type] || 0) + Number(a.days_count || 0);
    });
    return acc;
  }, [myAbsences]);

  const teamAbsences = useMemo(
    () => absences.filter((a) => a.employee_profile_id !== employeeProfileId && a.status === "approved"),
    [absences, employeeProfileId],
  );

  const markedByDate = useMemo(() => {
    const map = {};
    Object.entries(holidaysByDate).forEach(([date, name]) => {
      map[date] = { kind: "holiday", title: name };
    });
    teamAbsences.forEach((a) => {
      const start = ymdToLocalDate(a.start_date);
      const end = ymdToLocalDate(a.end_date);
      const cursor = new Date(start);
      while (cursor <= end) {
        const ymd = localDateToYmd(cursor);
        if (!map[ymd]) {
          const empName = a.employee_name || "Compañero";
          map[ymd] = {
            kind: "team-absence",
            title: `${empName}: ${ABSENCE_TYPE_LABEL[a.type] || a.type}`,
            avatarName: empName,
            subtitle: ABSENCE_TYPE_LABEL[a.type] || a.type,
          };
        }
        cursor.setDate(cursor.getDate() + 1);
      }
    });
    myAbsences.forEach((a) => {
      if (a.status === "rejected" || a.status === "cancelled") return;
      const start = ymdToLocalDate(a.start_date);
      const end = ymdToLocalDate(a.end_date);
      const cursor = new Date(start);
      while (cursor <= end) {
        const ymd = localDateToYmd(cursor);
        const prevTitle = map[ymd]?.title;
        const typeLabel = ABSENCE_TYPE_LABEL[a.type] || a.type;
        const titleParts = [typeLabel];
        if (prevTitle && prevTitle !== titleParts[0]) titleParts.push(prevTitle);
        map[ymd] = {
          kind: a.status === "approved" ? "absence-approved" : "absence-pending",
          title: titleParts.join(" · "),
          avatarName: a.employee_name || null,
          subtitle: typeLabel + (a.status === "pending" ? " (pendiente)" : ""),
          status: a.status,
        };
        cursor.setDate(cursor.getDate() + 1);
      }
    });
    return map;
  }, [holidaysByDate, myAbsences, teamAbsences]);

  const onCancel = async (a) => {
    const ok = await confirm({
      title: "Cancelar ausencia",
      description: `¿Seguro que quieres cancelar tu ${ABSENCE_TYPE_LABEL[a.type] || "ausencia"} del ${formatRange(a.start_date, a.end_date)}?`,
      confirmLabel: "Cancelar ausencia",
      cancelLabel: "No",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await cancelAbsence(a.id);
      toast.success("Ausencia cancelada");
      loadAbsences();
      if (employeeProfileId) loadBalance(employeeProfileId);
    } catch (e) {
      toast.error("No se pudo cancelar", typeof e === "string" ? e : e?.message);
    }
  };

  if (bootLoading) {
    return (
      <div className="kr-page" style={{ display: "flex", justifyContent: "center", padding: 48 }}>
        <Spinner />
      </div>
    );
  }
  if (bootError) {
    return (
      <div className="kr-page">
        <Alert tone="danger">{bootError}</Alert>
      </div>
    );
  }
  if (!employeeProfileId) {
    return (
      <div className="kr-page">
        <EmptyState
          icon="Calendar"
          title="Sin perfil de empleado"
          description="Tu cuenta no tiene perfil de empleado en este workspace, por lo que no puedes solicitar ni consultar ausencias. Si necesitas acceso, pide al responsable que cree tu perfil de empleado."
        />
      </div>
    );
  }

  return (
    <div className="kr-page">
      <PageHeader
        eyebrow="Personal"
        title="Ausencias"
        description="Solicita vacaciones y consulta tu saldo del año. Centralizado y siempre actualizado."
        actions={
          <span data-tour="ausencias-add">
            <Button variant="primary" icon="Plus" onClick={() => setShowNew(true)}>
              Añadir ausencia
            </Button>
          </span>
        }
      />

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "minmax(360px, 0.9fr) 1.6fr",
          gap: 16,
          alignItems: "start",
        }}
      >
        <div data-tour="ausencias-balance" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <CounterCard
            title="Contador de vacaciones"
            range={balance?.cycle_start && balance?.cycle_end ? formatRange(balance.cycle_start, balance.cycle_end) : "Ciclo actual"}
            unit={balance?.unit === "hours" ? "Horas" : "Días"}
            unlimited={Boolean(balance?.unlimited)}
            allowance={balance?.allowance}
            annualAllowance={(Number(balance?.base_allowance) || 0) + (Number(balance?.tenure_bonus) || 0)}
            used={balance?.used}
            pending={balance?.pending}
            available={balance?.available}
          />

          <Card>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--kr-fg-strong)" }}>Resumen por tipo</div>
              <span style={{ fontSize: 11, color: "var(--kr-fg-muted)" }}>{year}</span>
            </div>
            {Object.keys(breakdownByType).length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", padding: "4px 0" }}>
                Sin ausencias aprobadas todavía.
              </div>
            ) : (
              Object.entries(breakdownByType).map(([type, used], i, arr) => (
                <BreakdownRow
                  key={type}
                  label={ABSENCE_TYPE_LABEL[type] || type}
                  used={used}
                  last={i === arr.length - 1}
                />
              ))
            )}
          </Card>

          <Card>
            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--kr-fg-strong)", marginBottom: 10 }}>
              Próximas ausencias
            </div>
            {upcoming.length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", padding: "4px 0" }}>
                Sin ausencias próximas.
              </div>
            ) : (
              upcoming.map((a, i) => (
                <div
                  key={a.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "8px 0",
                    borderBottom: i < upcoming.length - 1 ? "1px solid var(--kr-border-subtle)" : "none",
                  }}
                >
                  <span
                    style={{
                      width: 8, height: 8, borderRadius: 999,
                      background: a.status === "approved" ? "var(--kr-action)" : "var(--kr-warning-500)",
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: "var(--kr-fg-strong)", fontWeight: 500 }}>
                      {ABSENCE_TYPE_LABEL[a.type] || a.type}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--kr-fg-muted)" }}>
                      {formatRange(a.start_date, a.end_date)} · {formatDays(a.days_count)}
                    </div>
                  </div>
                  <Badge tone={ABSENCE_STATUS_TONE[a.status]} dot>
                    {ABSENCE_STATUS_LABEL[a.status]}
                  </Badge>
                </div>
              ))
            )}
          </Card>
        </div>

        <Card>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <IconBtn icon="ChevronLeft" onClick={() => setYear((y) => y - 1)} />
              <span style={{ fontFamily: "var(--kr-font-display)", fontSize: 22, fontWeight: 700, color: "var(--kr-fg-strong)", minWidth: 70, textAlign: "center" }}>
                {year}
              </span>
              <IconBtn icon="ChevronRight" onClick={() => setYear((y) => y + 1)} />
            </div>
            <div style={{ display: "flex", gap: 14, fontSize: 11, color: "var(--kr-fg-muted)", flexWrap: "wrap" }}>
              <Legend color="var(--kr-success-500)" label="Aprobada" />
              <Legend color="#facc15" label="Pendiente" />
              <Legend color="var(--kr-info-100)" border="var(--kr-info-300)" label="Equipo" />
              <Legend color="var(--kr-danger-500)" label="Festivo" />
              <Legend ring="var(--kr-action)" label="Hoy" />
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(3, 1fr)",
              gap: isMobile ? 12 : 24,
            }}
          >
            {MESES.map((m, mi) => (
              <MiniMes key={mi} year={year} name={m} idx={mi} markedByDate={markedByDate} />
            ))}
          </div>
        </Card>
      </div>

      <div data-tour="ausencias-list">
      <Card padded={false}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--kr-border-subtle)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--kr-fg-strong)" }}>Mis ausencias</div>
          {error && <Badge tone="danger">{error}</Badge>}
        </div>
        {loading ? (
          <div style={{ padding: 32, textAlign: "center" }}><Spinner size={16} /></div>
        ) : myAbsences.length === 0 ? (
          <EmptyState
            icon="Calendar"
            title="Aún no has solicitado ausencias"
            description="Solicita la primera y aparecerá aquí con su estado."
            action={{ label: "Añadir ausencia", icon: "Plus", onClick: () => setShowNew(true) }}
          />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1.4fr 80px 1.6fr 110px 90px",
                padding: "10px 20px",
                background: "var(--kr-neutral-50)",
                borderBottom: "1px solid var(--kr-border)",
                fontSize: 11,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                fontWeight: 600,
                color: "var(--kr-fg-muted)",
                minWidth: 720,
              }}
            >
              <span>Tipo</span>
              <span>Fechas</span>
              <span>Días</span>
              <span>Motivo</span>
              <span>Estado</span>
              <span style={{ textAlign: "right" }}></span>
            </div>
            {myAbsences.map((a, i) => (
              <div
                key={a.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1.4fr 80px 1.6fr 110px 90px",
                  gap: 12,
                  alignItems: "center",
                  padding: "12px 20px",
                  borderBottom: i === myAbsences.length - 1 ? "none" : "1px solid var(--kr-border-subtle)",
                  fontSize: 13,
                  minWidth: 720,
                }}
              >
                <Badge tone={ABSENCE_TYPE_TONE[a.type] || "neutral"}>
                  {ABSENCE_TYPE_LABEL[a.type] || a.type}
                </Badge>
                <span style={{ color: "var(--kr-fg-strong)", fontFamily: "var(--kr-font-mono)" }}>
                  {formatRange(a.start_date, a.end_date)}
                </span>
                <span style={{ fontFamily: "var(--kr-font-mono)", fontWeight: 600, color: "var(--kr-fg-strong)" }}>
                  {formatDays(a.days_count)}
                </span>
                <span style={{ fontSize: 12, color: "var(--kr-fg-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {a.reason || "—"}
                </span>
                <Badge tone={ABSENCE_STATUS_TONE[a.status]} dot>
                  {ABSENCE_STATUS_LABEL[a.status]}
                </Badge>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  {(a.status === "pending" || a.status === "approved") && (
                    <Button variant="ghost" size="sm" icon="X" onClick={() => onCancel(a)}>
                      Cancelar
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
      </div>

      <NewAbsenceModal
        open={showNew}
        employeeProfileId={employeeProfileId}
        onClose={() => setShowNew(false)}
        onCreated={() => {
          loadAbsences();
          if (employeeProfileId) loadBalance(employeeProfileId);
        }}
      />
    </div>
  );
}

function CounterCard({ title, range, unit, unlimited, allowance, annualAllowance, used, pending, available }) {
  // El saldo se reduce respecto al derecho ANUAL cuando hay prorrateo por alta a
  // mitad de ciclo o devengo proporcional aún no completado. Lo avisamos para que
  // el número menor no se perciba como un error.
  const prorated = !unlimited
    && Number.isFinite(Number(annualAllowance))
    && Number(annualAllowance) > Number(allowance) + 0.01;
  return (
    <Card>
      <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>
        {title}
      </div>
      <div style={{ fontSize: 12, color: "var(--kr-fg-muted)", marginTop: 4 }}>{range}</div>
      {prorated && (
        <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", marginTop: 4 }}>
          Prorrateado · {numberOrDash(allowance)} de {numberOrDash(annualAllowance)} {unit.toLowerCase()} al año
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginTop: 14 }}>
        <Mini label={`${unit} disponibles`} value={unlimited ? "∞" : numberOrDash(available)} tone="default" />
        <Mini label="Aprobadas" value={numberOrDash(used)} tone="success" />
        <Mini label="Pendientes" value={numberOrDash(pending)} tone="warning" />
      </div>
      {!unlimited && Number(allowance) > 0 && (
        <div style={{ marginTop: 12 }}>
          <div
            style={{
              height: 6, background: "var(--kr-neutral-100)", borderRadius: 999,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${Math.min(100, (Number(used || 0) / Number(allowance)) * 100)}%`,
                height: "100%",
                background: "var(--kr-action)",
              }}
            />
          </div>
          <div style={{ fontSize: 11, color: "var(--kr-fg-muted)", marginTop: 6, fontFamily: "var(--kr-font-mono)" }}>
            {numberOrDash(used)} / {numberOrDash(allowance)} {unit.toLowerCase()}
          </div>
        </div>
      )}
    </Card>
  );
}

function numberOrDash(v) {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

const TONE_COLOR = {
  default: "var(--kr-fg-strong)",
  success: "var(--kr-success-700)",
  warning: "#a16207",  // yellow-700: amarillo oscuro legible (no naranja)
};

function Mini({ label, value, tone = "default" }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: "var(--kr-fg-muted)", textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
      <div
        style={{
          fontFamily: "var(--kr-font-mono)",
          fontSize: 22,
          fontWeight: 700,
          color: TONE_COLOR[tone] || TONE_COLOR.default,
          marginTop: 2,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function BreakdownRow({ label, used, last }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "6px 0",
        borderBottom: last ? "none" : "1px solid var(--kr-border-subtle)",
        fontSize: 13,
      }}
    >
      <span style={{ color: "var(--kr-fg-strong)" }}>{label}</span>
      <span style={{ fontFamily: "var(--kr-font-mono)", color: "var(--kr-fg-strong)", fontWeight: 600 }}>
        {numberOrDash(used)}d
      </span>
    </div>
  );
}

function Legend({ color, ring, border, label }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span
        style={{
          width: 10, height: 10, borderRadius: 999,
          background: color || "transparent",
          border: ring ? `2px solid ${ring}` : border ? `1px solid ${border}` : "none",
        }}
      />
      {label}
    </span>
  );
}

function CalendarCell({ day, ymd, marked, isToday }) {
  const [hover, setHover] = useState(false);
  let bg = "transparent";
  let color = "var(--kr-fg-strong)";
  if (marked?.kind === "absence-approved") {
    bg = "var(--kr-success-500)";
    color = "#fff";
  } else if (marked?.kind === "absence-pending") {
    bg = "#facc15";
    color = "#713f12";
  } else if (marked?.kind === "team-absence") {
    bg = "var(--kr-info-100)";
    color = "var(--kr-info-700)";
  } else if (marked?.kind === "holiday") {
    bg = "var(--kr-status-danger-bg)";
    color = "var(--kr-danger-700)";
  }

  return (
    <span
      style={{
        textAlign: "center",
        padding: "3px 0",
        fontSize: 11,
        background: bg,
        color,
        borderRadius: 4,
        outline: isToday ? "2px solid var(--kr-action)" : "none",
        outlineOffset: -1,
        fontFamily: "var(--kr-font-mono)",
        position: "relative",
        cursor: marked ? "default" : undefined,
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {day}
      {hover && marked && (
        <span style={{
          position: "absolute",
          bottom: "calc(100% + 4px)",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 100,
          background: "var(--kr-bg-surface)",
          border: "1px solid var(--kr-border)",
          borderRadius: 8,
          boxShadow: "0 4px 12px rgba(0,0,0,.12)",
          padding: "8px 10px",
          whiteSpace: "nowrap",
          pointerEvents: "none",
          display: "flex",
          alignItems: "center",
          gap: 8,
          minWidth: 120,
        }}>
          {marked.avatarName && (
            <span style={{
              width: 22, height: 22, borderRadius: 999, flexShrink: 0,
              background: "var(--kr-neutral-200)", display: "flex",
              alignItems: "center", justifyContent: "center",
              fontSize: 9, fontWeight: 700, color: "var(--kr-fg-muted)",
              fontFamily: "var(--kr-font-sans)",
            }}>
              {(marked.avatarName || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()}
            </span>
          )}
          <span style={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: "var(--kr-fg-strong)", fontFamily: "var(--kr-font-sans)" }}>
              {marked.avatarName || marked.title}
            </span>
            {marked.subtitle && (
              <span style={{ fontSize: 10, color: "var(--kr-fg-muted)", fontFamily: "var(--kr-font-sans)" }}>
                {marked.subtitle}
              </span>
            )}
          </span>
        </span>
      )}
    </span>
  );
}

function MiniMes({ year, name, idx, markedByDate }) {
  const offset = firstDayOffset(year, idx);
  const dim = daysInMonth(year, idx);
  const cells = [];
  for (let i = 0; i < offset; i++) cells.push(null);
  for (let d = 1; d <= dim; d++) cells.push(d);

  const todayYmd = localDateToYmd(new Date());

  return (
    <div>
      <div
        style={{
          fontSize: 12, fontWeight: 600, color: "var(--kr-fg-strong)",
          textTransform: "capitalize", marginBottom: 6,
        }}
      >
        {name}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, fontSize: 10, color: "var(--kr-fg-muted)" }}>
        {DIAS_SEM.map((d) => (
          <span key={d} style={{ textAlign: "center", padding: "2px 0" }}>{d}</span>
        ))}
        {cells.map((d, i) => {
          if (d === null) return <span key={i} />;
          const ymd = `${year}-${String(idx + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
          const marked = markedByDate[ymd];
          const isToday = ymd === todayYmd;
          return <CalendarCell key={i} day={d} ymd={ymd} marked={marked} isToday={isToday} />;
        })}
      </div>
    </div>
  );
}

export default Ausencias;
